import { createContext, h } from 'preact';
import { useContext, useEffect, useRef } from 'preact/hooks';
import { api } from 'api';
import { uwStatistics } from 'helpers/uw-statistics';
import { configStore } from 'global/config.store';
import { PROFILE_CONFIG } from 'constants/profileConfig';
import { ALLY_FEATURES } from 'constants/ally-features';
import { ApplicationContext } from './use-application-setup';

const STATISTICS_ENDPOINT = 'v1/stats/enabled-feature';
const STATISTICS_ADM_ENDPOINT = 'abn/widget-adm';

export enum StatisticsEvent {
  WidgetManageLogoutClick = 'widget_manage_logout_click',
  WidgetManageOpenRemediationsForReview = 'widget_manage_open_remediations_for_review',
  WidgetManageOpenPdfRemediations = 'widget_manage_open_pdf_remediations',
  WidgetManageOpenFocusStyle = 'widget_manage_open_focus_style',
  WidgetManageOpenAccessibilityEditor = 'widget_manage_open_accessibility_editor',
  WidgetManageGetReportClick = 'widget_manage_get_report_click',
}

export const StatisticsContext = createContext<ReturnType<typeof useStatistics>>(null);

export const useStatistics = () => {
  const {
    thisSiteId,
    accountIdCode,
    isClosedByUser,
    paidAi,
    userId,
    config,
    widgetLayout,
    tunings,
    targetPageOrigin,
  } = useContext(ApplicationContext);
  const isOnPrem = config.current.onPrem;
  const isMobile = config.current.isMobile;
  const statsRequestWaitList = useRef<string[]>([]);

  const enabledFeatures = useRef({});

  const sendEnableFeatureStats = (feat: string, real: boolean = true) => {
    if (isOnPrem) return;
    api
      .post(STATISTICS_ENDPOINT, {
        uid: userId,
        siteId: thisSiteId,
        account: accountIdCode,
        type: paidAi ? 'paid' : 'free',
        feat,
        real,
        widgetLayout,
      })
      .catch((e) => console.error(e));
  };

  const sendEventStats = async (eventName: StatisticsEvent): Promise<void> => {
    // disable statistic tracking when strict privacy mode is enabled
    if (tunings?.strict_privacy_mode) return;

    try {
      await api.post(STATISTICS_ADM_ENDPOINT, {
        events: [
          {
            event: 'pload',
            timestamp: +new Date(),
            content: eventName,
            uId: userId,
            page: new URL(targetPageOrigin).hostname,
            device: isMobile ? 'm' : 'd',
            user_source: tunings.isLAUser ? 'LAP' : 'UserWay',
          },
        ],
      });
    } catch (err) {
      console.error('Failed to send statistics', err);
    }
  };

  const sendStatistics = (feature: string, real: boolean = true) => {
    // disable statistic tracking when strict privacy mode is enabled
    if (tunings?.strict_privacy_mode) return;

    if (thisSiteId) {
      sendEnableFeatureStats(feature, real);
      return;
    }

    // if client still did not receive config with site id then stats should be sent later
    statsRequestWaitList.current.push(feature);
  };

  useEffect(() => {
    if (thisSiteId && accountIdCode && statsRequestWaitList.current.length) {
      statsRequestWaitList.current.forEach((data) => {
        sendEnableFeatureStats(data);
      });
      statsRequestWaitList.current = [];
    }
  }, [thisSiteId, accountIdCode]);

  const onFeatureTrigger = (feature: string, actionState?: string | number) => {
    if (feature === 's8') {
      for (const feature in enabledFeatures.current) {
        if (enabledFeatures.current.hasOwnProperty(feature)) {
          enabledFeatures.current[feature] = false;
        }
      }
      return;
    }

    if (actionState !== 0) {
      const isProfileFeature = Boolean(PROFILE_CONFIG[feature]);
      const featureName = ALLY_FEATURES[feature].description;

      if (!tunings?.strict_privacy_mode) {
        if (isProfileFeature) {
          uwStatistics.profileTriggered({ data: featureName });
          for (const feature in enabledFeatures.current) {
            if (enabledFeatures.current.hasOwnProperty(feature)) {
              enabledFeatures.current[feature] = false;
            }
          }
        } else {
          uwStatistics.featureTriggered({ data: featureName });
        }
      }
    }

    // exclude feature that user turned off if that feature has already been turned on on load
    // (such a feature will not be on the map and the state will be 0)
    if (actionState === 0 && !enabledFeatures.current[feature]) {
      return;
    }

    enabledFeatures.current[feature] = actionState !== 0;
  };

  useEffect(() => {
    if (isClosedByUser) {
      const features = Object.keys(enabledFeatures.current);
      if (features.length) {
        features.forEach((feat) => {
          sendStatistics(feat, enabledFeatures.current[feat]);
        });
        enabledFeatures.current = {};
      }
    }
  }, [isClosedByUser, sendStatistics]);

  return {
    onFeatureTrigger,
    sendStatistics,
    sendEventStats,
  };
};

const WidgetOpenedTracker = () => {
  const { isOpen, tunings } = useContext(ApplicationContext);
  const { sendStatistics } = useContext(StatisticsContext);
  const isOpenedStatisticSent = useRef<boolean>(false);

  useEffect(() => {
    if (isOpenedStatisticSent.current || tunings?.strict_privacy_mode || !isOpen) {
      return;
    }

    uwStatistics.widgetOpened({ data: configStore.config$.lastValue.href });
    sendStatistics('opens');
    isOpenedStatisticSent.current = true;
  }, [isOpen, sendStatistics, tunings]);

  return null;
};

export const StatisticsProvider = ({ children }) => {
  const statistics = useStatistics();

  return (
    <StatisticsContext.Provider value={statistics}>
      <WidgetOpenedTracker />
      {children}
    </StatisticsContext.Provider>
  );
};
